import React, {useState, useMemo, useEffect} from 'react';
import {DrawerContainer, DrawerFilterContent, DrawerTitle, FilterItem, FilterWrapper} from "./styles";
import { ReactComponent as SortIcon } from "../../assets/images/sort.svg";
import StyledDrawer from "../CustomAntdComponents/StyledDrawer";
import {Button} from "antd";
import RoadFilters from "./RoadFilters";

const categories = ['Sort', 'Operators', 'Price'];

type Props = {
    handleOperatorFiltering?: (arg: string[]) => void,
    handleSort: (arg0: string | null) => void,
    farePriceRange: PriceRangeType,
    handleFilterByPriceRange: (arg0: [number, number]) => void,
    selectedOperators: string[],
    sortBy: string,
    operators: SelectOption[],
    openAllFilters?: boolean;
    setOpenAllFilters?: (val: boolean) => void;
    onApplyAllFilters?: (filters: {
        operators: string[];
        price: [number, number] | null;
        sortBy: string;
    }) => void;
}


const RoadListingFilterMobile = ({ handleOperatorFiltering, selectedOperators, operators, handleSort,
                                     farePriceRange, handleFilterByPriceRange, sortBy,
                                     openAllFilters, setOpenAllFilters, onApplyAllFilters
}: Props) => {
    const [active, setActive] = useState(categories[0])
    const [isOpen, setIsOpen] = useState(false)
    const [payload, setPayload] = useState({
        operators: selectedOperators,
        price: [farePriceRange.min, farePriceRange.max],
        sort: sortBy
    });

    useEffect(() => {
        if (openAllFilters) return;
        setPayload({
            operators: selectedOperators,
            price: [farePriceRange.min, farePriceRange.max],
            sort: sortBy
        })
    }, [selectedOperators, farePriceRange, sortBy, openAllFilters]);

    const needsConfirmation = useMemo(() => ['Operators', 'Price'].includes(active), [active])

    const handleFilterCallback = (currentSortValue = null) => {
        setIsOpen(false);

        switch (active) {
            case 'Operators':
                handleOperatorFiltering((currentSortValue === null) ? payload.operators : currentSortValue);
                break;
            case 'Price':
                handleFilterByPriceRange((currentSortValue === null) ?  payload.price : currentSortValue);
                break;
            case 'Sort':
                handleSort((currentSortValue === null) ? payload.sort : currentSortValue);
                break;
            default:
                break;
        }
    }

    const handleOpenDrawer = (category: string) => {
        setIsOpen(true)
        setActive(category)
    }

    const handleFilterChange = (name, value) => {
        setPayload({
            ...payload,
            [name]: value
        })

        if(!needsConfirmation) {
            setIsOpen(false);
            handleFilterCallback(value);
        }
    }

    const handleApplyAllFilters = () => {
        setOpenAllFilters?.(false);

        if (onApplyAllFilters) {
            onApplyAllFilters({
                operators: payload.operators,
                price: payload.price as [number, number],
                sortBy: payload.sort,
            });
        } else {
            handleOperatorFiltering?.(payload.operators);
            handleFilterByPriceRange?.(payload.price as [number, number]);
            handleSort?.(payload.sort);
        }
    };

    const handleResetAllFilters = () => {
        setPayload({
            operators: [],
            price: [farePriceRange.min, farePriceRange.max],
            sort: "cheapest",
        });
    };

    const AllFiltersFooter = (
        <div style={{ display: "flex", gap: "10px" }}>
            <Button block onClick={handleResetAllFilters} style={{ borderRadius: "100px", height: "48px" }}>
                Clear all
            </Button>
            <Button type="primary" block onClick={handleApplyAllFilters} style={{ borderRadius: "100px", height: "48px" }}>
                Apply
            </Button>
        </div>
    );

    const DrawerFooter = needsConfirmation && (
        <Button type="primary" block onClick={() => handleFilterCallback()}>
            Confirm
        </Button>
    )

    return (
        <>
            <FilterWrapper>
                {
                    categories.map((category) => (
                        <FilterItem key={category} onClick={() => handleOpenDrawer(category)} active={active === category}>
                            <SortIcon />
                            <span>
                            {category}
                        </span>
                        </FilterItem>
                    ))
                }
            </FilterWrapper>
            <StyledDrawer
                open={isOpen}
                onClose={() => setIsOpen(false)}
                footer={DrawerFooter}
            >
                <DrawerContainer>
                    <DrawerTitle align="center">
                        {active}
                    </DrawerTitle>
                    <DrawerFilterContent>
                        <RoadFilters
                            category={active}
                            operators={operators}
                            onFilterChange={handleFilterChange}
                            farePriceRange={farePriceRange}
                            selectedOperators={payload.operators}
                            sortBy={payload.sort}
                            selectedPriceRange={payload.price as [number, number]}
                        />
                    </DrawerFilterContent>
                </DrawerContainer>
            </StyledDrawer>

            <StyledDrawer
                open={openAllFilters}
                onClose={() => setOpenAllFilters?.(false)}
                footer={AllFiltersFooter}
            >
                <DrawerContainer>
                    <DrawerTitle align="center">All filters</DrawerTitle>
                    <DrawerFilterContent style={{ paddingBottom: "20px" }}>
                        {categories.map((category) => (
                            <div key={category} style={{ marginBottom: "20px" }}>
                                <h4 style={{ fontWeight: 600, fontSize: "16px", marginBottom: "15px" }}>
                                    {category === "Sort" ? "Sort by" : category}
                                </h4>
                                <RoadFilters
                                    category={category}
                                    operators={operators}
                                    onFilterChange={(name, value) => {
                                        setPayload((prev) => ({ ...prev, [name]: value }));
                                    }}
                                    farePriceRange={farePriceRange}
                                    selectedOperators={payload.operators}
                                    sortBy={payload.sort}
                                    selectedPriceRange={payload.price as [number, number]}
                                />
                            </div>
                        ))}
                    </DrawerFilterContent>
                </DrawerContainer>
            </StyledDrawer>
        </>
    );
};

export default RoadListingFilterMobile;
